/**
 * Recursive tree layout engine for the workflow visual editor.
 *
 * Computes absolute (x, y) positions for every node and edge
 * so the renderer can place them with `position: absolute` and
 * draw connectors in a single SVG overlay.
 */
import type { WorkflowBranch, WorkflowStep } from '../../types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const NODE_W = 280;
export const TRIGGER_W = 380;
export const NODE_H = 90;
export const TRIGGER_H = 90;
export const ADD_BTN_SIZE = 28;

export const GAP_X = 48;
export const GAP_Y = 40;
export const SPLIT_DROP_Y = 32;

// ---------------------------------------------------------------------------
// Dynamic height estimation
// ---------------------------------------------------------------------------

const ACTION_BASE_H = 84;
const DETAIL_LINE_H = 18;

function estimateActionHeight(step: Extract<WorkflowStep, { kind: 'action' }>): number {
  const action = step.action;
  let detailLines = 0;

  if (action.type === 'send-notification') {
    if (action.label) detailLines += 1;
  } else if (action.type === 'assign-template-forms') {
    if (action.formTemplateRefs?.length) detailLines += 1;
    if (action.assignmentRecipientPolicies?.length) detailLines += 1;
    if (action.assignmentDueOffsetDays !== undefined) detailLines += 1;
  } else if (action.type === 'set-claim-field') {
    if (action.claimFieldPath) detailLines += 1;
    if (action.claimFieldValue) detailLines += 1;
  }
  if (action.type !== 'set-claim-field' && action.timingBucket) detailLines += 1;

  return ACTION_BASE_H + detailLines * DETAIL_LINE_H;
}

const BRANCH_CARD_BASE_H = 92;

function estimateBranchCardHeight(branch: WorkflowBranch): number {
  if (branch.alwaysRun) {
    return BRANCH_CARD_BASE_H + DETAIL_LINE_H;
  }
  const conditionCount = branch.conditionGroups.reduce(
    (sum, g) => sum + g.conditions.length,
    0,
  );
  return BRANCH_CARD_BASE_H + conditionCount * DETAIL_LINE_H;
}

// ---------------------------------------------------------------------------
// Layout output types
// ---------------------------------------------------------------------------

export interface LayoutRect {
  id: string;
  kind: 'trigger' | 'action' | 'branch-card' | 'else-card' | 'add-btn';
  x: number;
  y: number;
  w: number;
  h: number;
  /** Payload carried through for rendering */
  data?: unknown;
}

export interface LayoutEdge {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface LayoutResult {
  width: number;
  height: number;
  nodes: LayoutRect[];
  edges: LayoutEdge[];
}

// ---------------------------------------------------------------------------
// Internal sizing helpers
// ---------------------------------------------------------------------------

interface SubtreeSize {
  w: number;
  h: number;
}

function sizeOfStepSequence(steps: WorkflowStep[]): SubtreeSize {
  let h = 0;
  let w = NODE_W;
  for (const step of steps) {
    if (step.kind === 'action') {
      h += GAP_Y + estimateActionHeight(step);
    } else {
      const splitSize = sizeOfBranchSplit(step);
      w = Math.max(w, splitSize.w);
      h += GAP_Y + splitSize.h;
    }
  }
  const lastStep = steps[steps.length - 1];
  if (!lastStep || lastStep.kind !== 'branch') {
    h += GAP_Y + ADD_BTN_SIZE;
  }
  return { w, h };
}

function sizeOfBranchLane(branch: WorkflowBranch): SubtreeSize {
  const stepsSize = sizeOfStepSequence(branch.steps);
  const cardH = estimateBranchCardHeight(branch);
  const h = SPLIT_DROP_Y + cardH + stepsSize.h;
  const w = Math.max(NODE_W, stepsSize.w);
  return { w, h };
}

function sizeOfElseLane(elseSteps: WorkflowStep[]): SubtreeSize {
  if (elseSteps.length === 0) {
    return {
      w: NODE_W,
      h: SPLIT_DROP_Y + NODE_H + GAP_Y + ADD_BTN_SIZE,
    };
  }
  const stepsSize = sizeOfStepSequence(elseSteps);
  return {
    w: Math.max(NODE_W, stepsSize.w),
    h: SPLIT_DROP_Y + NODE_H + stepsSize.h,
  };
}

function sizeOfBranchSplit(step: Extract<WorkflowStep, { kind: 'branch' }>): SubtreeSize {
  const laneSizes = step.branches.map(sizeOfBranchLane);
  const elseLaneSize = sizeOfElseLane(step.elseSteps);
  const allSizes = [...laneSizes, elseLaneSize];

  const totalW = allSizes.reduce((sum, s) => sum + s.w, 0) + GAP_X * (allSizes.length - 1);
  const maxLaneH = Math.max(...allSizes.map((s) => s.h));

  return { w: totalW, h: maxLaneH };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Top inset so the trigger doesn't sit flush against the container edge. */
export const LAYOUT_PAD_TOP = 20;

/**
 * Compute the full layout for a workflow.
 * The trigger is always at the top-center.
 * Steps follow below the trigger.
 */

export function computeLayout(
  steps: WorkflowStep[],
  hasSteps: boolean,
  triggerData?: unknown,
): LayoutResult {
  const nodes: LayoutRect[] = [];
  const edges: LayoutEdge[] = [];

  // Measure total width so we can center the trigger over the widest subtree
  let bodyW = TRIGGER_W;

  if (hasSteps && steps.length > 0) {
    for (const step of steps) {
      if (step.kind === 'branch') {
        const s = sizeOfBranchSplit(step);
        bodyW = Math.max(bodyW, s.w);
      }
    }
  }

  const totalW = Math.max(bodyW, TRIGGER_W);
  const centerX = totalW / 2;

  // Place trigger (offset by LAYOUT_PAD_TOP so edges and nodes share one coordinate space)
  const triggerX = centerX - TRIGGER_W / 2;
  nodes.push({
    id: '__trigger__',
    kind: 'trigger',
    x: triggerX,
    y: LAYOUT_PAD_TOP,
    w: TRIGGER_W,
    h: TRIGGER_H,
    data: triggerData,
  });

  let cursorY = LAYOUT_PAD_TOP + TRIGGER_H;

  if (!hasSteps || steps.length === 0) {
    return {
      width: totalW,
      height: cursorY + GAP_Y + 200,
      nodes,
      edges,
    };
  }

  // Layout each root step
  for (const step of steps) {
    if (step.kind === 'branch') {
      const splitSize = sizeOfBranchSplit(step);
      const splitCenterX = centerX;
      const splitTopY = cursorY;

      edges.push({
        x1: centerX,
        y1: splitTopY,
        x2: centerX,
        y2: splitTopY + GAP_Y,
      });

      layoutBranchSplit(step, splitCenterX, splitTopY + GAP_Y, splitSize.w, nodes, edges);
      cursorY = splitTopY + GAP_Y + splitSize.h;
    } else {
      edges.push({
        x1: centerX,
        y1: cursorY,
        x2: centerX,
        y2: cursorY + GAP_Y,
      });
      cursorY += GAP_Y;
      const actionH = estimateActionHeight(step);
      nodes.push({
        id: step.id,
        kind: 'action',
        x: centerX - NODE_W / 2,
        y: cursorY,
        w: NODE_W,
        h: actionH,
        data: step,
      });
      cursorY += actionH;
    }
  }

  // Root-level add-btn only when the last root step is NOT a branch group
  const lastRootStep = steps[steps.length - 1];
  if (!lastRootStep || lastRootStep.kind !== 'branch') {
    edges.push({ x1: centerX, y1: cursorY, x2: centerX, y2: cursorY + GAP_Y });
    cursorY += GAP_Y;
    nodes.push({
      id: '__root__add-step',
      kind: 'add-btn',
      x: centerX - ADD_BTN_SIZE / 2,
      y: cursorY,
      w: ADD_BTN_SIZE,
      h: ADD_BTN_SIZE,
      data: { isRoot: true, rootSteps: steps },
    });
    cursorY += ADD_BTN_SIZE;
  }

  return {
    width: totalW,
    height: cursorY + 40,
    nodes,
    edges,
  };
}

// ---------------------------------------------------------------------------
// Internal recursive layout
// ---------------------------------------------------------------------------

function layoutBranchSplit(
  step: Extract<WorkflowStep, { kind: 'branch' }>,
  centerX: number,
  topY: number,
  _totalW: number,
  nodes: LayoutRect[],
  edges: LayoutEdge[],
): void {
  const laneSizes = step.branches.map(sizeOfBranchLane);
  const elseLaneSize = sizeOfElseLane(step.elseSteps);
  const allSizes = [...laneSizes, elseLaneSize];
  const computedTotalW = allSizes.reduce((sum, s) => sum + s.w, 0) + GAP_X * (allSizes.length - 1);

  let laneX = centerX - computedTotalW / 2;
  const splitBarY = topY;

  // Layout each IF branch lane
  for (let i = 0; i < step.branches.length; i++) {
    const branch = step.branches[i];
    const laneW = allSizes[i].w;
    const laneCenterX = laneX + laneW / 2;

    if (Math.abs(laneCenterX - centerX) > 1) {
      edges.push({ x1: centerX, y1: splitBarY, x2: laneCenterX, y2: splitBarY });
    }
    edges.push({ x1: laneCenterX, y1: splitBarY, x2: laneCenterX, y2: splitBarY + SPLIT_DROP_Y });

    const branchCardY = splitBarY + SPLIT_DROP_Y;
    const branchCardH = estimateBranchCardHeight(branch);
    nodes.push({
      id: branch.id,
      kind: 'branch-card',
      x: laneCenterX - NODE_W / 2,
      y: branchCardY,
      w: NODE_W,
      h: branchCardH,
      data: { branch, branchIndex: i, splitId: step.id },
    });

    let stepY = branchCardY + branchCardH;
    for (const childStep of branch.steps) {
      edges.push({ x1: laneCenterX, y1: stepY, x2: laneCenterX, y2: stepY + GAP_Y });
      stepY += GAP_Y;

      if (childStep.kind === 'action') {
        const actionH = estimateActionHeight(childStep);
        nodes.push({
          id: childStep.id,
          kind: 'action',
          x: laneCenterX - NODE_W / 2,
          y: stepY,
          w: NODE_W,
          h: actionH,
          data: childStep,
        });
        stepY += actionH;
      } else {
        const nestedSize = sizeOfBranchSplit(childStep);
        layoutBranchSplit(childStep, laneCenterX, stepY, nestedSize.w, nodes, edges);
        stepY += nestedSize.h;
      }
    }

    // Add-step button at end of branch lane (only if last step isn't a branch group)
    const lastBranchStep = branch.steps[branch.steps.length - 1];
    if (!lastBranchStep || lastBranchStep.kind !== 'branch') {
      edges.push({ x1: laneCenterX, y1: stepY, x2: laneCenterX, y2: stepY + GAP_Y });
      stepY += GAP_Y;
      nodes.push({
        id: `${branch.id}__add-step`,
        kind: 'add-btn',
        x: laneCenterX - ADD_BTN_SIZE / 2,
        y: stepY,
        w: ADD_BTN_SIZE,
        h: ADD_BTN_SIZE,
        data: { branchId: branch.id, branchSteps: branch.steps },
      });
    }

    laneX += laneW + GAP_X;
  }

  // Else lane
  const elseLaneCenterX = laneX + elseLaneSize.w / 2;
  if (Math.abs(elseLaneCenterX - centerX) > 1) {
    edges.push({ x1: centerX, y1: splitBarY, x2: elseLaneCenterX, y2: splitBarY });
  }
  edges.push({ x1: elseLaneCenterX, y1: splitBarY, x2: elseLaneCenterX, y2: splitBarY + SPLIT_DROP_Y });

  const elseCardY = splitBarY + SPLIT_DROP_Y;
  nodes.push({
    id: `${step.id}__else`,
    kind: 'else-card',
    x: elseLaneCenterX - NODE_W / 2,
    y: elseCardY,
    w: NODE_W,
    h: NODE_H,
    data: { splitId: step.id, elseSteps: step.elseSteps },
  });

  let elseStepY = elseCardY + NODE_H;
  for (const childStep of step.elseSteps) {
    edges.push({ x1: elseLaneCenterX, y1: elseStepY, x2: elseLaneCenterX, y2: elseStepY + GAP_Y });
    elseStepY += GAP_Y;

    if (childStep.kind === 'action') {
      const actionH = estimateActionHeight(childStep);
      nodes.push({
        id: childStep.id,
        kind: 'action',
        x: elseLaneCenterX - NODE_W / 2,
        y: elseStepY,
        w: NODE_W,
        h: actionH,
        data: childStep,
      });
      elseStepY += actionH;
    } else {
      const nestedSize = sizeOfBranchSplit(childStep);
      layoutBranchSplit(childStep, elseLaneCenterX, elseStepY, nestedSize.w, nodes, edges);
      elseStepY += nestedSize.h;
    }
  }

  // Add-step button at end of else lane (only if last step isn't a branch group)
  const lastElseStep = step.elseSteps[step.elseSteps.length - 1];
  if (!lastElseStep || lastElseStep.kind !== 'branch') {
    edges.push({ x1: elseLaneCenterX, y1: elseStepY, x2: elseLaneCenterX, y2: elseStepY + GAP_Y });
    elseStepY += GAP_Y;
    nodes.push({
      id: `${step.id}__else-add-step`,
      kind: 'add-btn',
      x: elseLaneCenterX - ADD_BTN_SIZE / 2,
      y: elseStepY,
      w: ADD_BTN_SIZE,
      h: ADD_BTN_SIZE,
      data: { splitId: step.id, isElse: true },
    });
  }
}

// ---------------------------------------------------------------------------
// SVG path helper
// ---------------------------------------------------------------------------

export function makeEdgePath(e: LayoutEdge, radius = 8): string {
  const { x1, y1, x2, y2 } = e;
  if (x1 === x2) {
    return `M${x1},${y1} L${x2},${y2}`;
  }
  if (y1 === y2) {
    return `M${x1},${y1} L${x2},${y2}`;
  }
  const r = Math.min(radius, Math.abs(x2 - x1) / 2, Math.abs(y2 - y1) / 2 || radius);
  const dx = x2 > x1 ? 1 : -1;
  const dy = y2 > y1 ? 1 : -1;
  return [
    `M${x1},${y1}`,
    `L${x2 - dx * r},${y1}`,
    `Q${x2},${y1} ${x2},${y1 + dy * r}`,
    `L${x2},${y2}`,
  ].join(' ');
}
